<template>
  <div class="print-product-barcode">
    <KTCodePreview v-bind:title="'In mã vạch'">
      <template v-slot:toolbar>
        <button
          type="button"
          class="btn btn-primary font-weight-bolder btn-sm"
          @click="printData"
          style="width: max-content"
        >
          <i class="fas fa-print"></i> In mã vạch
        </button>
      </template>

      <template v-slot:preview>
        <div id="printMe" :style="barcodePreviewStyle">
          <div
            class="barcode-print-page"
            v-for="(group, index) in listProduct"
            :key="index"
          >
            <div
              class="barcode-label"
              v-for="(product, count) in group"
              :key="count"
            >
              <div class="barcode-label-inner">
                <p class="barcode-label-text barcode-product-code">
                  {{ product.productCode || product.barCode }}
                </p>

                <div class="barcode-graphic-wrap">
                  <barcode
                    :margin="0"
                    class="barcode-graphic"
                    :key="`${product.barCode}-${barCodeHeight}-${currentPrintLimit}`"
                    v-bind:value="product.barCode"
                    format="CODE128"
                    :width="getBarModuleWidth(product.barCode)"
                    :height="barCodeHeight"
                    :fontSize="getBarcodeFontSize"
                    :displayValue="false"
                    :marginTop="0"
                    :marginLeft="0"
                    :marginRight="0"
                    :marginBottom="0"
                    :textMargin="0"
                  />
                </div>
                <p class="barcode-label-text barcode-label-price">
                  Giá: <span class="barcode-label-price-value">{{ convertPrice(product.sellingPrice) }}</span>
                </p>

                <p
                  :class="[
                    'barcode-label-text',
                    'barcode-label-name',
                    { 'barcode-label-name-long': (product.productName || '').length > 40 },
                  ]"
                >
                  {{ product.productName }}
                </p>
              </div>
            </div>
          </div>
        </div>
      </template>
    </KTCodePreview>
  </div>
</template>

<script>
import KTCodePreview from '@/view/content/CodePreview.vue'
import { SET_BREADCRUMB } from '@/core/services/store/modules/breadcrumbs.module'
import VueBarcode from 'vue-barcode'
import { makeToastFaile } from '../../../utils/common'

export default {
  props: ['data'],
  data() {
    return {
      listProduct: [],
      TYPE: {
        BARCODE_1_TEM: 4,
        BARCODE_2_TEM: 2,
        BARCODE_3_TEM: 1,
      },
    }
  },
  created() {
    if (!this.data) {
      return makeToastFaile('Chưa có dữ liệu!')
    }
    this.genData()
  },
  computed: {
    currentPrintLimit() {
      switch (this.data.type) {
        case this.TYPE.BARCODE_1_TEM: return 1
        case this.TYPE.BARCODE_2_TEM: return 2
        case this.TYPE.BARCODE_3_TEM: return 3
        default: return 1
      }
    },
    getTemWidth() {
      return 40 * this.currentPrintLimit
    },
    getLabelHeightMm() {
      return 20
    },
    getBarcodeFontSize() {
      switch (this.currentPrintLimit) {
        case 1: return 7
        case 2: return 6
        case 3: return 6
        default: return 7
      }
    },
    /** Chiều cao vạch barcode (px) trong preview */
    barCodeHeight() {
      return 40
    },
    barCodeHeightMm() {
      return (this.barCodeHeight / 3.7795275591).toFixed(2)
    },
    barCodePrintHeight() {
      const scale = (20 * 3.7795275591) / 140
      return Math.round(this.barCodeHeight * scale)
    },
    barCodePrintHeightMm() {
      return (this.barCodePrintHeight / 3.7795275591).toFixed(2)
    },
    barcodePreviewStyle() {
      return {
        '--barcode-height-px': `${this.barCodeHeight}px`,
        '--barcode-height-mm': `${this.barCodeHeightMm}mm`,
      }
    },
  },
  methods: {
    /**
     * Tính module width động để barcode vừa khít label 40mm (trừ padding ~2mm mỗi bên).
     * CODE128: mỗi ký tự ~11 modules, start/stop/checksum ~35 modules tổng.
     * Target usable width = 36mm = ~136px tại 96dpi.
     */
    getBarModuleWidth(code) {
      if (!code) return 1.6
      const str = String(code)
      const totalModules = str.length * 11 + 35
      const targetPx = 136 // 36mm usable
      const mw = targetPx / totalModules
      return parseFloat(Math.min(2.4, Math.max(0.8, mw)).toFixed(2))
    },

    /**
     * Sau khi SVG được render, ép width="100%" + viewBox + preserveAspectRatio="none"
     * để barcode stretch full width container.
     * Phải gọi SAU $nextTick vì vue-barcode render async.
     */
    fixSvgViewBox() {
      this.$nextTick(() => {
        this._applySvgFix(document)
      })
    },

    _applySvgFix(rootEl) {
      rootEl.querySelectorAll('.barcode-graphic-wrap svg').forEach((svg) => {
        // Lấy kích thước thực trước khi override
        const w = parseFloat(svg.getAttribute('width') || svg.getBoundingClientRect().width || 200)
        const h = parseFloat(svg.getAttribute('height') || svg.getBoundingClientRect().height || 40)

        // Set viewBox để giữ nội dung khi stretch
        if (w > 0 && h > 0) {
          svg.setAttribute('viewBox', `0 0 ${w} ${h}`)
        }
        // preserveAspectRatio="none" → stretch theo cả 2 chiều, không giữ tỉ lệ
        svg.setAttribute('preserveAspectRatio', 'none')

        // Override width → CSS kiểm soát, height → CSS variable
        svg.setAttribute('width', '100%')
        svg.removeAttribute('height')
        svg.style.cssText += ';width:100%!important;display:block!important;'
      })
    },

    /**
     * Chuẩn hóa SVG trong clone trước khi gửi sang print window.
     * Dùng barCodePrintHeightMm cho height thực tế khi in.
     */
    normalizePrintBarcodes(rootEl) {
      const barHMm = this.barCodePrintHeightMm
      rootEl.querySelectorAll('.barcode-graphic-wrap svg').forEach((svg) => {
        const w = parseFloat(svg.getAttribute('width') || svg.clientWidth || 200)
        const h = parseFloat(svg.getAttribute('height') || svg.clientHeight || 40)

        if (w > 0 && h > 0) {
          svg.setAttribute('viewBox', `0 0 ${w} ${h}`)
        }
        svg.setAttribute('preserveAspectRatio', 'none')
        svg.setAttribute('width', '100%')
        svg.setAttribute('height', `${barHMm}mm`)
        svg.style.setProperty('width', '100%', 'important')
        svg.style.setProperty('max-width', '100%', 'important')
        svg.style.setProperty('height', `${barHMm}mm`, 'important')
        svg.style.setProperty('min-height', `${barHMm}mm`, 'important')
        svg.style.setProperty('display', 'block', 'important')
      })
    },

    printData() {
      const printMe = document.getElementById('printMe')
      if (!printMe) return

      const clone = printMe.cloneNode(true)
      this.normalizePrintBarcodes(clone)
      const prtHtml = clone.innerHTML

      const width = this.getTemWidth
      const labelH = this.getLabelHeightMm
      const barHMm = this.barCodePrintHeightMm

      const WinPrint = window.open(
        '', '',
        'left=0,top=0,width=800,height=900,toolbar=0,scrollbars=0,status=0',
      )

      WinPrint.document.write(`<!DOCTYPE html>
<html>
  <head>
    <style>
      html, body {
        margin: 0;
        padding: 0;
        font-family: Tahoma, Verdana, sans-serif;
      }
      @page {
        size: ${width}mm ${labelH}mm;
        margin: 0;
      }
      @media print {
        html, body {
          width: ${width}mm;
          height: auto !important;
          margin: 0 !important;
          padding: 0 !important;
          display: block !important;
          line-height: 1.1 !important;
          box-sizing: border-box;
          -webkit-print-color-adjust: exact;
          print-color-adjust: exact;
        }
      }
      .barcode-print-page {
        display: flex;
        flex-direction: row;
        align-items: stretch;
        justify-content: flex-start;
        width: ${width}mm;
        height: ${labelH}mm;
        min-height: ${labelH}mm;
        max-height: ${labelH}mm;
        box-sizing: border-box;
        page-break-inside: avoid;
        overflow: hidden;
      }
      .barcode-print-page:not(:last-child) {
        page-break-after: always;
        break-after: page;
      }
      .barcode-label {
        flex: 0 0 40mm;
        width: 40mm !important;
        height: ${labelH}mm !important;
        min-width: 40mm !important;
        max-width: 40mm !important;
        min-height: ${labelH}mm !important;
        max-height: ${labelH}mm !important;
        margin: 0 !important;
        box-sizing: border-box;
        display: flex !important;
        flex-direction: column;
        align-items: stretch;
        justify-content: flex-start;
        overflow: hidden;
        background: #ffffff !important;
      }
      .barcode-label-inner {
        flex: 1 1 auto;
        width: 100% !important;
        height: 100%;
        min-height: 0;
        box-sizing: border-box;
        padding: 1.5mm 2mm 0 1.5mm;
        display: flex;
        flex-direction: column;
        justify-content: flex-start;
        align-items: center;
        text-align: center;
        overflow: hidden;
        background: #ffffff !important;
      }
      .barcode-label-text {
        color: #000 !important;
        font-weight: 400;
        text-align: center;
        margin: 0;
        padding: 0 1mm;
        width: 100%;
        max-width: 100%;
        box-sizing: border-box;
        word-wrap: break-word;
        overflow-wrap: anywhere;
        flex-shrink: 1;
        min-height: 0;
      }
      .barcode-product-code {
        font-size: 8px !important;
        line-height: 1 !important;
        font-weight: 500;
        letter-spacing: 0 !important;
        margin: 0 0 0.5mm 0 !important;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
        flex-shrink: 0;
      }
      .barcode-label-price {
        font-size: 8px !important;
        font-weight: 400;
        margin-top: 1mm !important;
        margin-bottom: 0 !important;
        flex-shrink: 0;
      }
      .barcode-label-price-value {
        font-weight: 700 !important;
      }
      .barcode-label-name {
        font-size: 8px !important;
        line-height: 1.1 !important;
        font-weight: 400;
        margin-top: 0.1mm !important;
        margin-bottom: 0 !important;
        max-height: 3.3em;
        display: -webkit-box;
        line-clamp: 3;
        -webkit-line-clamp: 3;
        -webkit-box-orient: vertical;
        overflow: hidden;
      }
      .barcode-label-name-long {
        font-size: 8px !important;
        line-height: 1 !important;
        max-height: 3em;
        line-clamp: 3;
        -webkit-line-clamp: 3;
      }

      /* ── Barcode SVG: full width, no whitespace ── */
      .barcode-graphic-wrap {
        display: block !important;
        width: 100% !important;
        max-width: 100% !important;
        margin: 0 !important;
        padding: 0 !important;
        line-height: 0;
        overflow: hidden;
        flex-shrink: 0;
      }
      .barcode-graphic-wrap .vue-barcode-element {
        display: block !important;
        width: 100% !important;
        max-width: 100% !important;
        line-height: 0 !important;
        margin: 0 !important;
      }
      /* SVG đã được normalizePrintBarcodes set viewBox + preserveAspectRatio="none"
         nên width: 100% sẽ stretch full, height cố định = barHMm */
      .barcode-graphic-wrap svg {
        display: block !important;
        width: 100% !important;
        max-width: 100% !important;
        height: ${barHMm}mm !important;
        min-height: ${barHMm}mm !important;
        max-height: ${barHMm}mm !important;
        margin: 0 !important;
        overflow: visible;
      }
      @media print {
        .barcode-graphic-wrap svg {
          width: 100% !important;
          max-width: 100% !important;
          height: ${barHMm}mm !important;
          min-height: ${barHMm}mm !important;
          max-height: ${barHMm}mm !important;
        }
      }
    </style>
  </head>
  <body>${prtHtml}</body>
</html>`)

      WinPrint.document.close()
      const triggerPrint = () => {
        WinPrint.focus()
        WinPrint.print()
      }
      if (WinPrint.document.readyState === 'complete') {
        triggerPrint()
      } else {
        WinPrint.onload = triggerPrint
      }
    },

    convertPrice(number) {
      return new Intl.NumberFormat('vn-VN').format(number)
    },

    genData() {
      const limit = this.currentPrintLimit
      const listItem = this.data.listItem ? this.data.listItem : []
      const listProductPrint = []

      listItem.forEach((element) => {
        const quantity = parseInt(element.quantity)
        for (let i = 0; i < quantity; i++) {
          listProductPrint.push({
            id: element.id,
            barCode: element.productCode,
            productName: element.productName,
            sellingPrice: element.sellingPrice,
          })
        }
      })

      const numPage = Math.ceil(listProductPrint.length / limit)
      this.listProduct = []
      for (let page = 1; page <= numPage; page++) {
        const start = limit * (page - 1)
        const end = page * limit
        this.listProduct.push(listProductPrint.slice(start, end))
      }
    },
  },
  components: {
    KTCodePreview,
    barcode: VueBarcode,
  },
  mounted() {
    this.$store.dispatch(SET_BREADCRUMB, [
      { title: 'Sản phẩm', route: '/products' },
      { title: 'In sản phẩm', route: '/products/print-product' },
      { title: 'In mã vạch' },
    ])
    this.fixSvgViewBox()
  },
  updated() {
    this.fixSvgViewBox()
  },
}
</script>

<style scoped>
.barcode-label {
  width: min(280px, 100%) !important;
  max-width: 100%;
  aspect-ratio: 40 / 20;
  box-sizing: border-box;
  display: flex !important;
  flex-direction: column;
  align-items: stretch;
  justify-content: flex-start;
  overflow: hidden;
  background: #ffffff;
  border: 1px solid #eceff3;
  font-family: Tahoma, Verdana, sans-serif;
}
.barcode-label-inner {
  flex: 1 1 auto;
  width: 100% !important;
  height: 100%;
  min-height: 0;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  align-items: center;
  box-sizing: border-box;
  padding: 8px 8px 0 8px;
  text-align: center;
  overflow: hidden;
  background: #ffffff;
}
.barcode-label-text {
  color: black;
  font-weight: 400;
  text-align: center;
  margin: 0;
  padding: 0 4px;
  width: 100%;
  max-width: 100%;
  box-sizing: border-box;
  word-wrap: break-word;
  overflow-wrap: anywhere;
}
.barcode-product-code {
  font-size: 16px;
  line-height: 1;
  font-weight: 500;
  letter-spacing: 0;
  margin: 0 0 2px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  flex-shrink: 0;
}
.barcode-label-price {
  font-size: 16px;
  line-height: 1.1;
  font-weight: 400;
  margin: 2px 0 -2px;
  flex-shrink: 0;
}
.barcode-label-price-value {
  font-weight: 700;
}
.barcode-label-name {
  font-size: 16px;
  line-height: 1.1;
  font-weight: 400;
  margin-top: 2px;
  margin-bottom: 0;
  max-height: 3.3em;
  display: -webkit-box;
  line-clamp: 3;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
}
.barcode-label-name-long {
  font-size: 16px;
  line-height: 1;
  max-height: 3em;
  line-clamp: 3;
  -webkit-line-clamp: 3;
}

/* ── Barcode wrap: block (không dùng flex để tránh shrink SVG) ── */
.barcode-graphic-wrap {
  display: block;
  width: 100%;
  max-width: 100%;
  flex-shrink: 0;
  margin: 0;
  padding: 0;
  line-height: 0;
  overflow: hidden;
}

.barcode-print-page {
  width: 100%;
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  justify-content: center;
  align-items: stretch;
  gap: 8px;
  margin-bottom: 1rem;
}
.barcode-print-page .barcode-label {
  flex: 0 0 auto;
  margin-bottom: 0;
}
</style>

<style lang="scss">
.print-product-barcode {
  #printMe {
    overflow-x: visible;
  }

  /* vue-barcode wrapper: block, full width */
  .barcode-graphic-wrap .vue-barcode-element {
    display: block !important;
    width: 100% !important;
    max-width: 100% !important;
    margin: 0 !important;
    line-height: 0 !important;
  }

  /*
   * SVG: width 100% để stretch full container.
   * height được kiểm soát bởi CSS variable --barcode-height-px.
   * preserveAspectRatio="none" được set bởi fixSvgViewBox() → stretch không méo code,
   * chỉ phần whitespace margin của vue-barcode bị compress.
   */
  .barcode-graphic-wrap svg {
    display: block !important;
    width: 100% !important;
    max-width: 100% !important;
    height: var(--barcode-height-px, 40px) !important;
    min-height: var(--barcode-height-px, 40px) !important;
    max-height: var(--barcode-height-px, 40px) !important;
    margin: 0 !important;
    overflow: visible;
  }
}
</style>